// src/components/FunFact.tsx - Beautiful enhanced version
import React, { useState } from 'react';
import Enhanced3DElement from './Enhanced3DElement';
import { ELEMENT_DEFS } from './elementDefinitions';
import { getLocalFunFact } from '../services/funFactService';
import { colors } from '../theme/designTokens';

interface FunFactProps {
  /** The two (or more) ingredient IDs that were combined */
  ingredients: string[];
  /** The ID of the element you just discovered */
  result: string;
}

export default function FunFact({ ingredients, result }: FunFactProps) {
  const [enabled, setEnabled] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  // Don't render at all if dismissed
  if (dismissed) return null;

  // build list: each ingredient then the result
  const items = [
    ...ingredients.map(id => ({
      id,
      title: ELEMENT_DEFS[id]?.name || id,
      fact: getLocalFunFact(id, ingredients) || '',
      isResult: false,
    })),
    {
      id: result,
      title: ELEMENT_DEFS[result]?.name || result,
      fact: getLocalFunFact(result, ingredients) || '',
      isResult: true,
    },
  ];

  const isMobile = window.innerWidth < 768;

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: isMobile ? 'max(env(safe-area-inset-bottom), 5.5rem)' : '1.5rem',
    left: isMobile ? '0.75rem' : 'auto',
    right: isMobile ? '0.75rem' : '1.5rem',
    top: isMobile ? 'max(env(safe-area-inset-top), 4rem)' : 'auto',
    maxWidth: isMobile ? 'auto' : '450px',
    minWidth: isMobile ? 'auto' : '350px',
    maxHeight: isMobile ? 'auto' : '50vh',
    display: 'flex',
    flexDirection: 'column',
    background: `linear-gradient(135deg, ${colors.background.card}dd 0%, ${colors.background.secondary}dd 100%)`,
    borderRadius: '1rem',
    border: `2px solid ${colors.brand.accent}40`,
    backdropFilter: 'blur(10px)',
    boxShadow: `0 8px 24px rgba(0,0,0,0.3), 0 0 20px ${colors.brand.accent}20`,
    animation: 'fadeIn 0.5s ease-out',
    zIndex: 100,
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    background: `${colors.background.card}ee`,
    borderBottom: `1px solid ${colors.brand.accent}30`,
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    zIndex: 2,
  };

  const titleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '1.1rem',
    fontWeight: 700,
    background: colors.gradients.gold,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  };

  const toggleButtonStyle: React.CSSProperties = {
    padding: '0.4rem 0.75rem',
    borderRadius: '0.5rem',
    background: enabled ? colors.background.tertiary : colors.brand.accent,
    color: colors.text.primary,
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontWeight: 600,
    transition: 'all 0.3s ease',
    flexShrink: 0,
  };

  return (
    <div style={containerStyle}>
      {/* Animated background glow */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '200%',
          height: '200%',
          background: `radial-gradient(circle, ${colors.brand.accent}15 0%, transparent 70%)`,
          animation: 'pulse 3s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />

      {/* Sticky Header */}
      <div style={headerStyle}>
        <h3 style={titleStyle}>
          <span style={{ fontSize: '1.2rem' }}>🔍</span>
          <span>Discovery Insights</span>
        </h3>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            onClick={() => setEnabled(e => !e)}
            style={toggleButtonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 4px 12px ${colors.brand.accent}50`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {enabled ? 'Hide' : 'Show'}
          </button>
          <button
            onClick={() => setDismissed(true)}
            aria-label="Close discovery insights"
            style={{
              background: '#EF4444',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              padding: '0.5rem',
              minWidth: '44px',
              minHeight: '44px',
              fontSize: '1.25rem',
              fontWeight: 'bold',
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)',
              transition: 'all 0.3s ease',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.6)';
              e.currentTarget.style.background = '#DC2626';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.4)';
              e.currentTarget.style.background = '#EF4444';
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={{ position: 'relative', zIndex: 1, flex: 1, overflowY: 'auto', padding: '1rem' }}>
        {enabled ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '0.75rem',
            }}
          >
            {items.map(({ id, title, fact, isResult }) => (
              <div
                key={id}
                style={{
                  padding: '0.75rem',
                  borderRadius: '0.65rem',
                  background: isResult
                    ? `linear-gradient(135deg, ${colors.brand.accent}20 0%, ${colors.brand.accent}10 100%)`
                    : `${colors.background.tertiary}80`,
                  border: isResult
                    ? `2px solid ${colors.brand.accent}`
                    : `1px solid ${colors.background.tertiary}`,
                  boxShadow: isResult
                    ? `0 4px 12px ${colors.brand.accent}30`
                    : '0 2px 8px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease',
                  cursor: 'default',
                  animation: `fadeIn ${0.3 + items.indexOf({ id, title, fact, isResult }) * 0.1}s ease-out`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = isResult
                    ? `0 8px 20px ${colors.brand.accent}50`
                    : '0 6px 16px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = isResult
                    ? `0 4px 12px ${colors.brand.accent}30`
                    : '0 2px 8px rgba(0,0,0,0.2)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: `radial-gradient(circle at 30% 30%, ${colors.background.card}, ${colors.background.tertiary})`,
                      padding: '6px',
                      border: isResult ? `2px solid ${colors.brand.accent}` : `1px solid ${colors.background.tertiary}`,
                      boxShadow: isResult ? `0 0 15px ${colors.brand.accent}50` : 'none',
                      flexShrink: 0,
                    }}
                  >
                    <Enhanced3DElement e={ELEMENT_DEFS[id]} size={28} intensity="subtle" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        color: isResult ? colors.brand.accent : colors.text.primary,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                      }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
                      {isResult && (
                        <span
                          style={{
                            fontSize: '0.65rem',
                            padding: '0.1rem 0.4rem',
                            borderRadius: '0.75rem',
                            background: colors.brand.accent,
                            color: 'white',
                            flexShrink: 0,
                          }}
                        >
                          NEW!
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <p
                  style={{
                    fontSize: '0.8rem',
                    lineHeight: 1.4,
                    color: colors.text.secondary,
                    margin: 0,
                  }}
                >
                  {fact || 'No description available'}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              textAlign: 'center',
              padding: '1.5rem',
              color: colors.text.tertiary,
            }}
          >
            <p style={{ marginBottom: '0.5rem', fontSize: '0.85rem' }}>Insights are hidden</p>
          </div>
        )}
      </div>
    </div>
  );
}
